/**
 * Hook per derivare i descriptor entita' dal registro filtrando per plugin
 * abilitato. Due varianti, stesso meccanismo sottostante (useEnabledPlugins):
 *
 *  - useEnabledEntityTypes(): core + plugin abilitati. Per chi tratta core
 *    e plugin uniformemente (search globale TopBar).
 *  - useEnabledPluginEntityTypes(): SOLO plugin abilitati (no core). Per
 *    chi renderizza pannelli aggiuntivi rispetto a quelli core hardcoded
 *    (RelationPanel nella colonna destra di Person/BusinessDetail).
 *  - useRelationTargetEntityTypes(): core + plugin abilitati, filtrati per
 *    `relationTarget`. E' la lista dei bersagli relazionali (TS-80).
 *
 * Regola generale: chi deve filtrare per plugin abilitato usa uno di
 * questi hook, mai inline flatMap.
 */

import { useMemo } from 'react';
import type { EntityTypeDescriptor } from '@crm/contracts';
import { useEnabledPlugins } from './useEnabledPlugins';
import { getPlugins } from './registry';
import { getAllEntityTypes } from '../entityRegistry/entityRegistry';
import { useDisabledEntityTypes } from './useCoreItems';

export function useEnabledEntityTypes(): EntityTypeDescriptor[] {
  const enabledPlugins = useEnabledPlugins();
  // I tipi delle voci del core spente sulla company (FILO-53): fuori come quelli dei plugin spenti.
  const disabledCoreTypes = useDisabledEntityTypes();
  return useMemo(() => {
    const enabledIds = new Set(enabledPlugins.map((p) => p.id));
    // Insieme dei typeName contribuiti da plugin registrati ma NON abilitati.
    // I core entityTypes (registrati da coreEntityRegistration, non da plugin)
    // non passano per questa lista -> sono sempre disponibili.
    const disabledTypeNames = new Set<string>();
    for (const p of getPlugins()) {
      if (!enabledIds.has(p.id)) {
        for (const et of p.entityTypes ?? []) {
          disabledTypeNames.add(et.typeName);
        }
      }
    }
    for (const typeName of disabledCoreTypes) disabledTypeNames.add(typeName);
    return getAllEntityTypes().filter((et) => !disabledTypeNames.has(et.typeName));
  }, [enabledPlugins, disabledCoreTypes]);
}

export function useEnabledPluginEntityTypes(): EntityTypeDescriptor[] {
  const enabledPlugins = useEnabledPlugins();
  return useMemo(
    () => enabledPlugins.flatMap((p) => p.entityTypes ?? []),
    [enabledPlugins],
  );
}

/**
 * I tipi che si sono dichiarati bersagli relazionali (`relationTarget: true`),
 * limitati ai plugin abilitati. Source-of-truth per "cosa puo' comparire come
 * pannello relazione o nel picker Associa ai record". Ha gia' sostituito la
 * costante CORE_ASSOCIABLE_TYPES del binder; restano da convertire i due gruppi
 * di pannelli scritti a mano in Person/BusinessDetail (TS-80 Punto 3).
 *
 * Un plugin spento fa sparire i suoi target da solo: il filtro per abilitazione
 * e' gia' in useEnabledEntityTypes(), qui si aggiunge solo l'opt-in.
 */
export function useRelationTargetEntityTypes(): EntityTypeDescriptor[] {
  const entityTypes = useEnabledEntityTypes();
  return useMemo(() => entityTypes.filter((et) => et.relationTarget), [entityTypes]);
}
